import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import { maskTail } from './types';

/**
 * A bank number, hidden until somebody asks for it.
 *
 * `businesses/accounts` returns account and routing numbers in the clear, and
 * the original rendered both as plain table columns and plain text inputs — so
 * they were on screen for anyone walking past, and in every screenshot of the
 * page.
 *
 * Nothing here changes what the API sends; it changes how long it sits visible.
 * Revealing is a click, and the field returns to masked when the drawer closes
 * because the state lives with the component.
 */
export function SecretField({
  label,
  htmlFor,
  hint,
  value,
  onChange,
  disabled,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);

  return (
    <Field label={label} htmlFor={htmlFor} hint={hint}>
      <div className="flex gap-2">
        {shown ? (
          <Input
            id={htmlFor}
            value={value}
            disabled={disabled}
            className="font-mono text-sm"
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          // A disabled input rather than `type="password"`: this is not a
          // password field, and browsers offer to save the ones that are.
          <Input
            id={htmlFor}
            readOnly
            value={maskTail(value)}
            className="text-muted-foreground font-mono text-sm"
            aria-label={`${label} — ${t('businessAccounts.hidden')}`}
          />
        )}
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          aria-label={
            shown ? t('businessAccounts.hide') : t('businessAccounts.reveal')
          }
          onClick={() => setShown((current) => !current)}
        >
          {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
      </div>
    </Field>
  );
}
